import { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { cancelPreBudget } from "Requests/Partners/PreBudgets";
import { useProductCombos } from "Hooks/useProductCombos";
import { createToast } from "Utils/toastFunc";
import {
  convertBudgetToOrder,
  listProductsOnBudgetsCart,
} from "Requests/Partners/Budgets";
import { getUnitsUser } from "Requests/HomeOrder";
import { linkUnitToClientBudget } from "Requests/Budget";
import { BUDGETSTATUS } from "Constants/Budget";

const combosParams = {
  includeCombo: ["categories", "subcategories"],
  searchBudget: true,
};

export const useClientsBudgetService = () => {
  const [unitSelected, setUnitSelected] = useState<null | number>(null);
  const [openUnitModal, setOpenUnitModal] = useState(false);
  const [openHomeFeeModal, setOpenHomeFeeModal] = useState(false);
  const [modalProductsError, setModalProductsError] = useState(false);
  const [cancelModal, setCancelModal] = useState(false);
  const [openConvertOrderModal, setOpenConvertOrderModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [freevisitConfirmCallback, setFreevisitConfirmCallback] = useState<{
    resolved: (() => void) | null;
    rejected: (() => void) | null;
  }>({ rejected: null, resolved: null });
  const [enableUnitReq, setEnableUnitReq] = useState(false);

  const history = useHistory();

  const { id } = useParams<{ id: string }>();

  const { data, isLoading, isError } = useQuery({
    queryFn: () => listProductsOnBudgetsCart(Number(id)),
    queryKey: ["budget-cart-items", id],
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 2,
  });

  if (data?.status?.id === BUDGETSTATUS.converted) {
    createToast(
      "error",
      "Este orçamento está bloqueado para edição, pois já foi convertido em pedido!"
    );
  }

  const cancelBudget = useMutation({
    mutationFn: cancelPreBudget,
  });

  const client = useQueryClient();

  const units = useQuery({
    queryFn: getUnitsUser,
    queryKey: "user-units",
    refetchOnWindowFocus: false,
    enabled: enableUnitReq,
  });

  const { combos } = useProductCombos(combosParams);
  const { categories } = combos ?? {};

  const blockConvertButton =
    cancelBudget.isLoading ||
    !data?.unit?.id ||
    loading ||
    data?.status?.id === BUDGETSTATUS.converted;

  const blockUnitButton =
    units.isLoading ||
    units?.data?.length === 0 ||
    data?.status?.id === BUDGETSTATUS.converted ||
    loading;

  const blockCancelButton =
    data?.status?.id === BUDGETSTATUS.converted ||
    cancelBudget.isLoading ||
    loading;

  const cheeckFreeVisit = () => {
    return new Promise((resolve, reject) => {
      setOpenHomeFeeModal(true);
      setFreevisitConfirmCallback({
        rejected: () => reject("canceled"),
        resolved: () => resolve(""),
      });
    });
  };

  const moveToConvertOrder = () => {
    if (freevisitConfirmCallback.resolved) freevisitConfirmCallback.resolved();
    setFreevisitConfirmCallback({ rejected: null, resolved: null });
    setOpenHomeFeeModal(false);
  };

  const cancelConvertOrder = () => {
    if (freevisitConfirmCallback.rejected) freevisitConfirmCallback.rejected();
    setFreevisitConfirmCallback({ rejected: null, resolved: null });
    setOpenHomeFeeModal(false);
  };

  const onCancelBudget = async () => {
    try {
      if (!data?.pre_budget_id)
        return createToast(
          "error",
          "Não foi possível cancelar o orçamento, pois este orçamento não tem o ID do pré-orçamento"
        );

      await cancelBudget.mutateAsync(data?.pre_budget_id);
      createToast("success", "Orçamento cancelado com sucesso!");
      history.push("/orcamentos/parceiro/unidade");
    } catch (error) {
      createToast("error", "Não foi possível cancelar o orçamento");
    }
  };

  const onConvertOrder = async () => {
    try {
      // Verificar se há arquivos de pedido médico anexados
      const hasFiles = data?.files && data.files.length > 0;

      if (hasFiles) {
        // Abrir modal de confirmação
        setOpenConvertOrderModal(true);
        return;
      }

      // Se não houver arquivos, seguir com o fluxo normal
      setLoading(true);

      if (!data?.cli_pre_budget.free_visit) await cheeckFreeVisit();

      if (data) {
        await convertBudgetToOrder(Number(id));

        history.push("/parceiro/pedido/unidade");
      }
    } catch (error) {
      if (error === "canceled") return;
      createToast(
        "error",
        "Não foi possível realizar a conversão deste pré-orçamento"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmConvertOrder = async () => {
    try {
      setOpenConvertOrderModal(false);
      setLoading(true);

      if (!data?.cli_pre_budget.free_visit) await cheeckFreeVisit();

      if (data) {
        await convertBudgetToOrder(Number(id));

        history.push("/parceiro/pedido/unidade");
      }
    } catch (error) {
      if (error === "canceled") return;
      createToast(
        "error",
        "Não foi possível realizar a conversão deste pré-orçamento"
      );
    } finally {
      setLoading(false);
    }
  };

  const onSelectUnit = async () => {
    try {
      setLoading(true);
      if (!unitSelected) return;
      await linkUnitToClientBudget(Number(id), unitSelected);
      client.refetchQueries(["budget-cart-items", id]);
      createToast("success", "Unidade adicionada com sucesso");
      setOpenUnitModal(false);
    } catch (error) {
      createToast("error", "Não foi possível vincular esta unidade");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (data?.errors) {
      setModalProductsError(true);
    }
  }, [data?.errors]);

  useEffect(() => {
    if (!data?.unit) {
      setEnableUnitReq(true);
    }
  }, [data]);

  return {
    isRetrivingBudget: isLoading,
    data,
    setOpenUnitModal,
    cancelBudget,
    unitSelected,
    onConvertOrder,
    openUnitModal,
    categories,
    setCancelModal,
    setUnitSelected,
    openHomeFeeModal,
    cancelConvertOrder,
    onSelectUnit,
    setOpenHomeFeeModal,
    moveToConvertOrder,
    modalProductsError,
    setModalProductsError,
    cancelModal,
    onCancelBudget,
    units,
    loading,
    blockConvertButton,
    blockUnitButton,
    blockCancelButton,
    isError,
    openConvertOrderModal,
    setOpenConvertOrderModal,
    handleConfirmConvertOrder,
  };
};
